// tcs-variant-request-guard: a failed variant request must not leave Add to bag
// pointing at the previous variant.
//
// Horizon 4.1.3 VariantPicker checks the new radio and rewrites ?variant= before
// it fetches the section. When that fetch REJECTS (offline, connection reset),
// ProductSelectEvent.promise rejects and ProductFormComponent keeps the old
// hidden id with Add enabled. Meanwhile the swatch, the URL and (with
// tcs-pdp-variant-media) the hero all show the new shade, so Add to bag would add
// a shade the shopper did not choose. An HTTP error page is already safe: Horizon
// parses it, finds no variant JSON, clears the id and disables Add.
//
// This guard applies those same Horizon semantics to a rejection: clear the
// form's id, disable its Add button container, hide express checkout, and
// disable the sticky Add to cart bar's button (which otherwise stays enabled,
// "clicks" the disabled main button and plays the fly-to-cart animation for an
// add that never happens; the same goes for Horizon's own HTTP-error path, so the
// sticky button follows a cleared, disabled main form there too). It acts only
// on the LATEST request of a scope, never
// on an AbortError (a newer selection superseded it), and never on product
// cards. It restores nothing on the main form: Horizon's next successful
// response (any reselection, or the same-swatch retry in tcs-pdp-variant-media)
// sets the id, enables or disables Add by availability, and un-hides express
// checkout. The sticky button is the one thing it restores, because Horizon only
// re-renders the bar when the section response carries one: on the next success
// the sticky button follows the main Add button's state (syncSticky).
//
// Scope mirrors ProductFormComponent, which listens on
// closest('.shopify-section, dialog, product-card'). So the guard covers the PDP,
// the featured-product section on any template, and the quick-add dialog (which
// Horizon renders from layout/theme.liquid outside every .shopify-section). It is
// loaded on every non-password page from snippets/tcs-head.liquid for that
// reason. Product cards stay out: TCS cards (tcs-card.js) resolve a shade on the
// client without a section request, and no stored template places a Horizon card
// whose swatches feed a direct-add form. See the evidence README for the limits.
//
// Evidence: docs/accessibility/evidence/2026-10-01/stale-variant-response/.
import { StandardEvents } from '@shopify/events';

const SCOPE = '.shopify-section, dialog';
const FAILED = 'data-tcs-variant-request-failed';
const PRODUCT_PICKER = "#MainContent[data-template^='product'] variant-picker";
const SHADE_GROUP = 'fieldset.variant-option--swatches';

/** @type {WeakMap<Element, Event>} */
const latest = new WeakMap();

/**
 * Give each PDP swatch fieldset one explicit, current accessible name.
 *
 * Horizon retains the fieldset node across a variant morph and updates the
 * selected value inside its legend. Safari 26 VoiceOver can keep the original
 * legend-derived group name after that descendant-only change. The checked
 * radio carries the option name and value already shown by the legend, so an
 * explicit aria-label makes the accessible-name update unambiguous without a
 * second live announcement.
 * @param {ParentNode} root
 */
function syncShadeGroupNames(root = document) {
  for (const picker of root.querySelectorAll(PRODUCT_PICKER)) {
    if (picker.closest('featured-product-information, quick-add-dialog, product-card')) continue;
    for (const fieldset of picker.querySelectorAll(SHADE_GROUP)) {
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      const optionName = selected?.dataset.optionName?.trim();
      const optionValue = selected?.value?.trim();
      if (optionName && optionValue)
        fieldset.setAttribute('aria-label', `${optionName} ${optionValue}`);
    }
  }
}

/** @param {Event & { promise?: Promise<unknown> }} event */
function syncShadeGroupNameOnSelect(event) {
  const target = event.target;
  const picker = target instanceof Element ? target.closest(PRODUCT_PICKER) : null;
  if (!picker) return;

  // The checked state changes before ProductSelectEvent is dispatched.
  syncShadeGroupNames(document);

  // The resolved promise follows Horizon's morph. Re-apply the explicit name
  // because the server markup does not carry runtime attributes.
  event.promise?.then(
    () => syncShadeGroupNames(document),
    () => {},
  );
}

// Product ids are compared as bare numbers. The real @shopify/events
// ProductSelectEvent rewrites event.product.id into a GID
// ("gid://shopify/Product/123"), while product-form-component and
// sticky-add-to-cart carry the numeric id Liquid renders. The PRIMARY source is
// therefore the dispatching variant-picker's own data-product-id, read when the
// event is dispatched: it is the exact value Horizon itself matches forms
// against (detail.productId in variant-picker.js -> #onProductSelect in
// product-form.js), and it does not depend on how @shopify/events encodes ids.
// event.product.id is only a fallback, with any GID prefix stripped.
/** @param {unknown} value */
const numericId = (value) =>
  String(value ?? '')
    .replace(/^gid:\/\/shopify\/Product\//, '')
    .trim();

/** @param {Element} el @param {string} productId */
const sameProduct = (el, productId) =>
  !productId ||
  !(el instanceof HTMLElement) ||
  !el.dataset.productId ||
  numericId(el.dataset.productId) === productId;

/** @param {Element} scope @param {string} productId */
function invalidate(scope, productId) {
  for (const form of scope.querySelectorAll('product-form-component')) {
    if (form.closest('product-card') || !sameProduct(form, productId)) continue;
    const id = form.querySelector('form input[name="id"]');
    if (id instanceof HTMLInputElement) id.value = '';
    const container = form.querySelector('[ref="addToCartButtonContainer"]');
    if (container && typeof container.disable === 'function') container.disable();
    else container?.querySelector('[ref="addToCartButton"]')?.setAttribute('disabled', '');
    form
      .querySelector('[ref="acceleratedCheckoutButtonContainer"]')
      ?.setAttribute('hidden', 'true');
  }
  for (const sticky of scope.querySelectorAll('sticky-add-to-cart')) {
    if (!sameProduct(sticky, productId)) continue;
    const button = sticky.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) continue;
    button.disabled = true;
    button.setAttribute(FAILED, '');
  }
}

// Runs after Horizon has handled a successful response. Two cases:
// - the guard disabled the sticky button after a rejection: it now follows the
//   main Add button again (Horizon re-renders the bar only when the response
//   carries one);
// - Horizon itself cleared the id and disabled Add (an HTTP error page, or no
//   such variant) but left the bar's button enabled, because the response had no
//   bar to morph from: disable it, so it cannot "add" with the fly-to-cart
//   animation while the main button is disabled.
/** @param {Element} scope @param {string} productId */
function syncSticky(scope, productId) {
  const form = [...scope.querySelectorAll('product-form-component')].find(
    (el) => !el.closest('product-card') && sameProduct(el, productId),
  );
  const main = form?.querySelector('[ref="addToCartButton"]');
  if (!(main instanceof HTMLButtonElement)) return;
  const id = form.querySelector('form input[name="id"]');
  const cleared = id instanceof HTMLInputElement && id.value === '';
  for (const sticky of scope.querySelectorAll('sticky-add-to-cart')) {
    if (!sameProduct(sticky, productId)) continue;
    const button = sticky.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) continue;
    if (button.hasAttribute(FAILED)) {
      button.removeAttribute(FAILED);
      button.disabled = main.disabled;
    }
    if (cleared && main.disabled && !button.disabled) {
      button.disabled = true;
      button.setAttribute(FAILED, '');
    }
  }
}

/** @param {Event & { promise?: Promise<unknown>, product?: { id?: unknown } }} event */
function onSelect(event) {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.closest('product-card')) return;
  const scope = target.closest(SCOPE);
  if (!scope || !(event.promise instanceof Promise)) return;
  const productId = numericId(target.dataset.productId || event.product?.id);
  latest.set(scope, event);
  const current = () => latest.get(scope) === event && scope.isConnected;
  event.promise.then(
    // Horizon's own handlers (form enable/disable, sticky morph) run as reactions
    // to the same promise; a macrotask lets them finish before the sticky button
    // copies the main button's state.
    () => setTimeout(() => current() && syncSticky(scope, productId)),
    (error) => {
      if (error?.name === 'AbortError' || !current()) return;
      invalidate(scope, productId);
    },
  );
}

if (!window.tcsVariantRequestGuard) {
  window.tcsVariantRequestGuard = true;
  syncShadeGroupNames(document);
  document.addEventListener(StandardEvents.productSelect, syncShadeGroupNameOnSelect, {
    capture: true,
  });
  document.addEventListener(StandardEvents.productSelect, onSelect, { capture: true });
}
